/**
 * Web App State Service Implementation
 * Uses window focus/blur and visibility events
 */

import type { AppStateService, AppStateListener, AppStateStatus } from './appStateService';

class WebAppStateService implements AppStateService {
  private listeners: Set<AppStateListener> = new Set();
  private _currentState: AppStateStatus = 'active';

  constructor() {
    // Set initial state based on document visibility
    this._currentState = document.hidden ? 'background' : 'active';

    // Listen to visibility changes
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // Listen to window focus/blur
    window.addEventListener('focus', this.handleFocus);
    window.addEventListener('blur', this.handleBlur);

    // Listen to page show/hide (for back/forward cache)
    window.addEventListener('pageshow', this.handlePageShow);
    window.addEventListener('pagehide', this.handlePageHide);
  }

  get currentState(): AppStateStatus {
    return this._currentState;
  }

  addEventListener(listener: AppStateListener): () => void {
    this.listeners.add(listener);

    // Return cleanup function
    return () => {
      this.removeEventListener(listener);
    };
  }

  removeEventListener(listener: AppStateListener): void {
    this.listeners.delete(listener);
  }

  isActive(): boolean {
    return this._currentState === 'active';
  }

  private handleVisibilityChange = () => {
    const newState: AppStateStatus = document.hidden ? 'background' : 'active';
    this.updateState(newState);
  };

  private handleFocus = () => {
    this.updateState('active');
  };

  private handleBlur = () => {
    // Check if document is still visible (might be switching tabs vs losing window focus)
    const newState: AppStateStatus = document.hidden ? 'background' : 'inactive';
    this.updateState(newState);
  };

  private handlePageShow = (event: PageTransitionEvent) => {
    // Page is shown (user navigated back using browser back button)
    if (event.persisted) {
      // Page was loaded from back/forward cache
      this.updateState('active');
    }
  };

  private handlePageHide = (event: PageTransitionEvent) => {
    // Page is hidden (user navigating away)
    this.updateState('background');
  };

  private updateState(newState: AppStateStatus) {
    if (this._currentState === newState) {
      return; // No change
    }

    this._currentState = newState;

    // Notify all listeners
    this.listeners.forEach(listener => {
      try {
        listener(newState);
      } catch (error) {
        console.error('[WebAppState] Error in listener:', error);
      }
    });
  }

  // Cleanup method
  destroy() {
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('focus', this.handleFocus);
    window.removeEventListener('blur', this.handleBlur);
    window.removeEventListener('pageshow', this.handlePageShow);
    window.removeEventListener('pagehide', this.handlePageHide);
    this.listeners.clear();
  }
}

// Export singleton instance
const appStateService = new WebAppStateService();
export default appStateService;
